import type { SceneConfig, SceneTransform } from './sceneConfig';
import type { SightId } from '../types';
import { resolveCdnUrl } from '../utils/cdnUrl';
import { KAABA_DEFAULT_COLLIDERS } from './collision/kaabaColliders';

const SCENE_SPZ_URL = resolveCdnUrl('models/scene.spz');
const KAABA_SPLAT_URL = resolveCdnUrl('models/kaaba.sog');
const KAABA_500K_SPLAT_URL = resolveCdnUrl('models/kaaba_500k.sog');
const KAABA_500K_V2_SPLAT_URL = resolveCdnUrl('models/kaaba_500k_v2.sog');
const ONLY_KAABA_SPLAT_URL = resolveCdnUrl('models/only_kaaba.sog');
const KAABA_500K_V3_SPLAT_URL = resolveCdnUrl('models/kaaba_500k_v3.sog');
const KAABA_DETAILED_SPLAT_URL = resolveCdnUrl('models/Kaaba_1500k_new_gate_sky_buildings.sog');
const KAABA_DETAILED_WITHOUT_SKY_SPLAT_URL = resolveCdnUrl('models/Kaaba_1500k_new_gate_buildings.sog_v2.sog');
const KAABA_DETAILED_WITHOUT_BUILDING_SPLAT_URL = resolveCdnUrl('models/Kaaba_1500k_new_gate_new.sog');
const KAABA_STABLE_SPLAT_URL = resolveCdnUrl('models/Kaaba_1500k_new_gate_stable.sog');
const KAABA_LOW_SCENE_600K_SPLAT_URL = resolveCdnUrl('models/kaaba_low_scene600k.sog');
const ALHARAM_SPLAT_URL = resolveCdnUrl('models/alharam_setuped.sog');

const PREVIEW_MODEL_TRANSFORM: SceneTransform = {
  rotation: { x: 0, y: 0, z: 0 },
  scale: { x: 0.02, y: 0.02, z: 0.02 }
};

const DEFAULT_VIRTUAL_TOUR_SIGHT_IDS: SightId[] = [
  // 'kaaba',
  'black-stone',
  'kiswah',
  'door',
  'golden-spout',
  'hijr-ismail',
  'maqam-ibrahim',
  // 'grand-mosque',
];

export const kaabaSceneConfig: SceneConfig = {
  id: 'kaaba',
  renderer: {
    clearColor: 0x0f0f13,
  },
  camera: {
    fov: 75,
    near: 0.01,
    far: 1e4,
    initialPosition: { x: 0, y: 0, z: 1 },
    previewTourStart: { x: -1.0851021763789943, y: 0.43560047138699426, z: 0.46662588628486934 },
    previewTourEnd: {
      position: { x: -0.711, y: 0.080, z: 0.064 },
      target: { x: -0.320, y: 0.110, z: 0.480 },
    },
    flyover: {
      delay: 1,
      segments: [
        // Rise from previewTourEnd up to orbit height
        {
          type: 'waypoint',
          position: { x: -0.740, y: 0.51, z: 0.046 },
          target: { x: -0.326, y: 0.150, z: 0.480 },
          duration: 1.7,
        },
        // Half-circle orbit at elevation (180°)
        {
          type: 'orbit',
          center: { x: -0.326, y: 0.150, z: 0.480 },
          radius: 0.6,
          height: 0.36,
          angleDeg: 180,
          duration: 3.3,
        },
        // Descent to final viewing position
        {
          type: 'waypoint',
          position: { x: -0.903, y: 0.080, z: 1.181 },
          target: { x: -0.326, y: 0.150, z: 0.480 },
          duration: 1.7,
        },
      ],
    },
  },
  controls: {
    orbitMinDistance: 0,
    orbitMaxDistance: 10,
    orbitMinPolarAngle: 0,
    orbitMaxPolarAngle: Math.PI,
    clampMinHeight: 0.08,
    clampMaxHeight: 0.43,
    clampMaxHeightFar: 0.20,
    constrainVertical: true,
    verticalMin: Math.PI * 1 / 3,
    verticalMax: Math.PI * 2 / 3,
    orbitCenter: { x: -0.32, y: 0.11, z: 0.48 },
  },
  colliders: KAABA_DEFAULT_COLLIDERS,
  splat: {
    // Desktop/mobile variants can diverge later; now both use the same file.
    defaultByQuality: {
      desktop: 'kaaba_500k',
      mobile: {
        high: 'kaaba_500k_v2',
        middle: 'kaaba_500k_v2',
        low: 'kaaba_low_scene600k',
      },
    },
    variants: [
      {
        key: 'scene',
        url: SCENE_SPZ_URL,
        aliases: ['scene.spz', '/scene.spz'],
      },
      {
        key: 'kaaba',
        url: KAABA_SPLAT_URL,
        aliases: ['kaaba.sog', '/kaaba.sog'],
      },
      {
        key: 'kaaba_500k',
        url: KAABA_500K_SPLAT_URL,
        aliases: ['kaaba_500k.sog', '/kaaba_500k.sog'],
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'kaaba_500k_v2',
        url: KAABA_500K_V2_SPLAT_URL,
        aliases: ['kaaba_500k_v2.sog', '/kaaba_500k_v2.sog'],
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'only_kaaba',
        url: ONLY_KAABA_SPLAT_URL,
        aliases: ['only_kaaba.sog', '/only_kaaba.sog'],
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'kaaba_500k_v3',
        url: KAABA_500K_V3_SPLAT_URL,
        aliases: ['kaaba_500k_v3.sog', '/kaaba_500k_v3.sog'],
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'kaaba_detailed',
        url: KAABA_DETAILED_SPLAT_URL,
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'kaaba_detailed_without_sky',
        url: KAABA_DETAILED_WITHOUT_SKY_SPLAT_URL,
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'kaaba_detailed_without_building',
        url: KAABA_DETAILED_WITHOUT_BUILDING_SPLAT_URL,
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
        skybox: {
          url: resolveCdnUrl('environment/skybox_with_buildings_light.jpg'),
          mobileUrl: resolveCdnUrl('environment/skybox_with_buildings_light.jpg'),
          intensity: 1.0,
          mobileIntensity: 1.0,
        },
      },
      {
        key: 'kaaba_stable',
        url: KAABA_STABLE_SPLAT_URL,
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'kaaba_low_scene600k',
        url: KAABA_LOW_SCENE_600K_SPLAT_URL,
        aliases: ['kaaba_low_scene600k.sog', '/kaaba_low_scene600k.sog'],
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'alharam_setuped',
        url: ALHARAM_SPLAT_URL,
        aliases: ['alharam_setuped.sog', '/alharam_setuped.sog'],
        // transform: PREVIEW_MODEL_TRANSFORM,
        // overrideDefaultTransform: true,
      },
    ],
    defaultTransform: {
      rotation: { x: 0, y: 0, z: Math.PI },
    },
    revealAnimationDuration: 3,
    revealDistanceScale: 5,
  },
  previewModel: {
    url: resolveCdnUrl('preview/kaaba_preview_1mb_meshopt.glb'),
    transform: PREVIEW_MODEL_TRANSFORM,
    style: {
      // Match pre_loader_II baseline (~3px on-screen). Без этого
      // gl_PointSize = sizeFromMat(1) × default(1) = 1px → бледно на retina.
      pointSizeFactor: 3.0,
      wavesCenter: { x: -0.32, y: 0, z: 0.48 },
      wavesMinDistance: 0.45,
      wavesAmplitude: 1.8,
      wavesPeriod: 1.0,
      wavesSpeed: 1.5,
      wavesColorIntensity: 5.0,
      wavesDisplacement: 0.03,
    },
  },
  assistant: {
    position: { x: 0, y: 0, z: -0.03 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    hemisphereLight: {
      skyColor: 0xffe6c4,
      groundColor: 0x1a2027,
      intensity: 0.45,
    },
    directionalLights: [
      {
        color: 0xfff2d5,
        intensity: 0.85,
        position: { x: -0.8, y: 1.2, z: 0.6 },
      },
      {
        color: 0xbdd4ff,
        intensity: 0.35,
        position: { x: 0.9, y: 0.6, z: -0.45 },
      },
    ],
    background: {
      color: 0x0f0f13,
      skybox: {
        url: resolveCdnUrl('environment/autumn_field_puresky_8k.jpg'),
        mobileUrl: resolveCdnUrl('environment/autumn_field_puresky_2k.jpg'),
        intensity: 1.0,
        blur: 0.0,
        rotation: [0, 205, 0]
      }
    },
  },
  virtualTour: DEFAULT_VIRTUAL_TOUR_SIGHT_IDS.map((id) => ({
    blocks: [],
    suggestions: [],
    actions: [{ type: 'focus-sight', sightId: id }],
  })),
};
